import { useEffect, useState } from 'react';
import { SceneBack } from '@/shared/components/SceneBack';
import { useTransition } from '@/shared/hooks/useCinematicTransition';
import { CHARACTERS, findCharacter, portraitFor } from '../data/characters';

/**
 * Spar flow entry — "Choose Your Adversary".
 *
 * Mirrors the Watch selection screen: the same roster grid, the same .pair
 * confirmation, the same gold CTA. Spar picks one figure (vs. the user)
 * instead of two; everything else is identical so the two flows feel like
 * the same gesture under a different framing.
 */
export function Spar() {
  const { fadeTo } = useTransition();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'The Ring — The Arena';
  }, []);

  const selected = findCharacter(selectedId);

  const handlePick = (id: string) => {
    setSelectedId((curr) => (curr === id ? null : id));
  };

  const handleEnter = () => {
    if (!selected) return;
    fadeTo({
      to: `/arena/spar/${selected.id}`,
      title: `Sparring ${selected.name} — The Arena`,
    });
  };

  return (
    <main className="scene scene--setup">
      <div
        className="scene__image scene__image--spar"
        role="img"
        aria-label="Gladiators walking into the arena through opened gates"
      />
      <div className="scene__vignette" />

      <SceneBack />

      <div className="scene__content scene__content--setup">
        <p className="scene__eyebrow">— Step Into the Ring —</p>
        <h1 className="scene__title scene__title--setup">
          Choose Your Adversary
        </h1>

        <ul className="roster" aria-label="Available adversaries">
          {CHARACTERS.map((c) => {
            const isSelected = selectedId === c.id;
            return (
              <li key={c.id} className="roster__item">
                <button
                  type="button"
                  className={`roster__btn${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handlePick(c.id)}
                  aria-pressed={isSelected}
                  title={c.blurb}
                >
                  <span className="roster__portrait" aria-hidden="true">
                    <img src={portraitFor(c.id)} alt="" loading="lazy" />
                  </span>
                  <span className="roster__name">{c.name}</span>
                  <span className="roster__era">{c.era}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {selected && (
          <>
            <p className="pair">
              <em className="pair__name">{selected.name}</em>
              <span className="pair__divider" aria-hidden="true">
                vs
              </span>
              <em className="pair__name">You</em>
            </p>

            <button
              type="button"
              className="watch-begin"
              onClick={handleEnter}
            >
              <span className="watch-begin__label">Enter the Ring</span>
              <span aria-hidden="true" className="watch-begin__arrow">
                →
              </span>
            </button>
          </>
        )}
      </div>
    </main>
  );
}
